import React, {
  forwardRef,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import styled from 'styled-components';
import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Autocomplete as AutocompleteBase,
  AutocompleteChangeDetails,
  AutocompleteChangeReason as MuiAutocompleteChangeReason,
  AutocompleteProps,
  AutocompleteRenderGetTagProps,
  Paper as PaperBase,
  PaperProps as PaperPropsBase,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  AutocompleteOwnerState,
  Box,
  Divider,
  FormHelperText,
  Popper,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { ReactComponent as MoreIcon } from '../../assets/more.svg';
import {
  ComponentSize,
  COMPONENT_SIZE_MAP,
  ValueLabelOption,
} from '../../types/types';
import IconButton from '../IconButton';
import TextField from '../TextField';
import Menu from '../Menu';
import Chip from '../Chip';
import { Flex } from '../Flex';
import FormControls from '../FormControls';

export type AutocompleteChangeReason =
  | MuiAutocompleteChangeReason
  | 'editOption';

export type AutocompleteOption<T = string> = {
  value: T;
  label: string | ReactNode;
  icon?: ReactNode;
  actions?: ValueLabelOption[];
};

type AutocompleteItem = string | AutocompleteOption;

const CHIP_HEIGHT: Record<ComponentSize, number> = {
  extraSmall: 24,
  medium: 24,
  large: 28,
};
const CHIP_GAP = 4;
const MAX_CHIP_ROWS = 3.5;
const EMPTY_ORDER: ReadonlyMap<string, number> = new Map();
const FIELD_PADDING_X: Record<ComponentSize, number> = {
  extraSmall: 10,
  medium: 10,
  large: 16,
};
/* Natural height of a single-select MUI Autocomplete input (7.5px padding
   top/bottom + 14px * 1.4375 line-height), which exceeds the small size tokens. */
const MUI_INPUT_HEIGHT = 35.125;
const fieldHeight = (size: ComponentSize) =>
  Math.max(COMPONENT_SIZE_MAP[size], MUI_INPUT_HEIGHT);
/* Vertical spacing that keeps a single chip row at the single-select height. */
const fieldPaddingY = (size: ComponentSize) =>
  (fieldHeight(size) - CHIP_HEIGHT[size]) / 2;
const NO_OPTIONS_TEXT = 'No matching options';

const StyledTextField = styled(TextField)<{
  $variant?: 'borderless';
  $chipDisplay?: boolean;
  $hasValue?: boolean;
}>`
  .MuiOutlinedInput-root {
    height: auto;

    .MuiAutocomplete-endAdornment {
      top: 50%;
      transform: translateY(-50%);
    }

    ${({ $chipDisplay, $variant }) =>
      $chipDisplay &&
      `
      flex-wrap: nowrap;
      padding-right: 0 !important;
      ${$variant === 'borderless' ? 'padding-left: 0;' : ''}
  
      .MuiAutocomplete-endAdornment {
        position: static;
        top: auto;
        transform: none;
        margin-left: -4px;
      }
    `}

    &.Mui-disabled {
      .MuiOutlinedInput-notchedOutline {
        background: ${({ theme }) => theme.palette.common.gray10} !important;
        border: none !important;
      }
    }

    ${({ $variant }) =>
      $variant === 'borderless' &&
      `
      .MuiOutlinedInput-notchedOutline {
        border: none !important;
      }

      &:hover .MuiOutlinedInput-notchedOutline {
        border: none !important;
      }

      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border: none !important;
      }
    `}

    ${({ $chipDisplay, $hasValue }) =>
      (!$chipDisplay || ($chipDisplay && $hasValue)) &&
      `
      .MuiAutocomplete-input {
        min-width: 0 !important;
        width: 0 !important;
      }
    `}
  }
`;

/* In multiple mode the input root scrolls; vertical padding is applied as margin
   so the scrollbar stays inside the rounded outline instead of being clipped by it. */
const Wrapper = styled.div<{
  $size: ComponentSize;
  $multiple: boolean;
  $fullWidth: boolean;
}>`
  display: ${({ $multiple }) => ($multiple ? 'block' : 'contents')};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  ${({ $multiple, $size, theme }) =>
    $multiple &&
    `
    .MuiFormControl-root {
      position: relative;
      isolation: isolate;
    }

    .MuiOutlinedInput-root {
      position: static;
      height: auto;
      min-height: ${CHIP_HEIGHT[$size]}px;
      max-height: ${
        MAX_CHIP_ROWS * CHIP_HEIGHT[$size] + (MAX_CHIP_ROWS - 1) * CHIP_GAP
      }px;
      margin: ${fieldPaddingY($size)}px 0;
      border-radius: 0;
      overflow-y: auto;
      align-items: flex-start;
      align-content: flex-start;
      gap: ${CHIP_GAP}px;
      padding: 0 ${FIELD_PADDING_X[$size]}px;

      &.Mui-disabled {
        background-color: transparent;

        .MuiOutlinedInput-notchedOutline {
          background-color: ${theme.palette.common.gray10};
          z-index: -1;
        }
      }

      .MuiAutocomplete-input {
        height: ${CHIP_HEIGHT[$size]}px;
        padding: 0 4px;
        min-width: 60px !important;
        width: 0 !important;
      }

      .MuiAutocomplete-endAdornment {
        top: ${fieldPaddingY($size) + (CHIP_HEIGHT[$size] - 28) / 2}px;
        transform: none;
      }
    }

    .MuiAutocomplete-tag {
      margin: 0;
      max-width: calc(100% - 8px);
    }

    .MuiFormHelperText-root {
      margin-left: 0;
    }
  `}
`;

const TagChip = styled(Chip)`
  color: ${({ theme }) => theme.palette.common.black};
`;

const EditInput = styled.input<{ $size: ComponentSize }>`
  height: ${({ $size }) => CHIP_HEIGHT[$size]}px;
  font: inherit;
  font-size: 14px;
  padding: 0 4px;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.palette.common.black};
  background: transparent;
`;

const SuggestionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 4px;
  max-height: 40vh;
  overflow-y: auto;
`;

const SuggestionItem = styled.li<{ $highlighted: boolean }>`
  font-size: 14px;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ $highlighted, theme }) =>
    $highlighted ? theme.palette.action.hover : 'transparent'};

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }
`;

const NoOptions = styled.div`
  font-size: 14px;
  padding: 14px 16px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const StyledActionItem = styled.div`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.common.blue};
  padding: 10px 16px;
  border-radius: 8px;
  background-color: white;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.common.gray9};
  }
`;

const InvalidChipWrapper = styled.span<{ $size: ComponentSize }>`
  display: inline-flex;
  align-items: center;
  height: ${({ $size }) => CHIP_HEIGHT[$size]}px;
  text-decoration: underline;
  text-decoration-style: dashed;
  text-decoration-color: ${({ theme }) => theme.palette.common.red2};
  padding: 0 8px;
  cursor: pointer;
`;

/** Identity key: strings and string-valued options share a key; other value types are tagged so `1` and `'1'` stay distinct. */
const itemValue = (item: AutocompleteItem): string => {
  if (typeof item === 'string') return item;
  if (typeof item.value === 'string') return item.value;
  if (item.value !== undefined && item.value !== null) {
    return `${typeof item.value}:${
      typeof item.value === 'object'
        ? JSON.stringify(item.value)
        : String(item.value)
    }`;
  }
  return typeof item.label === 'string' ? item.label : JSON.stringify(item);
};

const itemLabel = (item: AutocompleteItem): ReactNode =>
  typeof item === 'string' ? item : item.label;

/** Plain text representation used for matching and inline editing. */
const itemText = (item: AutocompleteItem): string => {
  if (typeof item === 'string') return item;
  return typeof item.label === 'string' ? item.label : String(item.value);
};

const itemIcon = (item: AutocompleteItem): React.ReactElement | undefined =>
  typeof item === 'string' || !React.isValidElement(item.icon)
    ? undefined
    : item.icon;

const isEditableItem = (item: AutocompleteItem) =>
  typeof item === 'string' || typeof item.label === 'string';

const normalizeText = (raw: string) => raw.trim();

const splitValues = (text: string, separator: string) =>
  text
    .split(separator)
    .map(normalizeText)
    .filter((v) => v.length > 0);

const dedupeItems = (items: AutocompleteItem[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = itemValue(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const defaultFilterOptions = createFilterOptions<AutocompleteItem>();
const EMPTY_OPTIONS: AutocompleteItem[] = [];

interface PaperProps extends PaperPropsBase {
  children?: ReactNode;
  actions?: string[];
  onAction?: (actionValue: string) => void;
  handleClose: (event: React.SyntheticEvent, reason: string) => void;
  noOptionsMessage?: ReactNode;
}

const Paper = ({
  children,
  actions,
  onAction,
  handleClose,
  noOptionsMessage,
  ...paperProps
}: PaperProps) => (
  <PaperBase {...paperProps}>
    {actions && actions.length > 0 && (
      <Box onMouseDown={(e) => e.preventDefault()} sx={{ padding: '4px' }}>
        {actions.map((action) => (
          <StyledActionItem
            key={action}
            onClick={(e) => {
              e.stopPropagation();
              onAction?.(action);
              handleClose(e, 'selectOption');
            }}
          >
            {action}
          </StyledActionItem>
        ))}
        <Box
          sx={{
            borderBottom: (theme) => `1px solid ${theme.palette.common.gray8}`,
            margin: '4px 0',
          }}
        />
      </Box>
    )}
    {noOptionsMessage ? <NoOptions>{noOptionsMessage}</NoOptions> : children}
  </PaperBase>
);

const OptionWithActions = ({
  props: listItemProps,
  option,
  keepOpen,
  onOptionAction,
  closeAutocomplete,
}: {
  props: React.HTMLAttributes<HTMLLIElement>;
  option: any;
  keepOpen: React.MutableRefObject<boolean>;
  onOptionAction?: (optionValue: string, actionValue: string) => void;
  closeAutocomplete: () => void;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const optionValue =
    typeof option === 'string' ? option : option.value ?? option;

  return (
    <li
      {...listItemProps}
      className={`${listItemProps.className || ''} ${
        isMenuOpen ? 'menu-open' : ''
      }`}
    >
      <Flex
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {option.label || option}
        </span>
        {option.actions && option.actions.length > 0 && (
          <Menu
            menuButton={
              <IconButton
                className="actions-menu-button"
                sx={{ p: '10px 6px !important' }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  keepOpen.current = true;
                }}
              >
                <MoreIcon />
              </IconButton>
            }
            options={option.actions}
            onOpen={() => setIsMenuOpen(true)}
            onChange={(actionValue) => {
              if (onOptionAction) {
                onOptionAction(optionValue, actionValue as string);
              }
              setIsMenuOpen(false);
              keepOpen.current = false;
              closeAutocomplete();
            }}
            onClose={() => {
              setIsMenuOpen(false);
              keepOpen.current = false;
            }}
          />
        )}
      </Flex>
    </li>
  );
};

const renderInput = (
  params: AutocompleteRenderInputParams,
  {
    ref,
    inputRef,
    autoFocus,
    placeholder,
    variant,
    chipDisplay,
    multiple,
    hasValue,
    value,
    size,
    label,
    name,
    error,
    helperTextId,
    onPaste,
  }: {
    ref: any;
    inputRef: React.MutableRefObject<HTMLInputElement | null>;
    autoFocus?: boolean;
    placeholder?: string;
    variant?: 'borderless';
    chipDisplay?: boolean;
    multiple?: boolean;
    hasValue: boolean;
    value: any;
    size?: ComponentSize;
    label?: string;
    name?: string;
    error?: boolean;
    helperTextId?: string;
    onPaste?: (event: React.ClipboardEvent) => void;
  },
) => {
  // For single-select, show the selected value's icon as startAdornment
  const selectedIcon =
    !multiple && value && typeof value === 'object' && value.icon
      ? value.icon
      : undefined;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { size: _muiSize, ...restParams } = params;

  return (
    <StyledTextField
      ref={ref}
      {...restParams}
      inputRef={inputRef}
      fullWidth
      size={size}
      name={name}
      label={label}
      error={error}
      autoFocus={autoFocus}
      placeholder={hasValue ? undefined : placeholder}
      onPaste={onPaste}
      $variant={variant}
      $chipDisplay={chipDisplay && !multiple}
      $hasValue={hasValue}
      inputProps={{
        ...params.inputProps,
        readOnly:
          chipDisplay && !multiple && value !== null && value !== undefined,
        'aria-describedby': helperTextId,
      }}
      InputProps={{
        ...params.InputProps,
        startAdornment: selectedIcon ? (
          <>
            <Flex sx={{ ml: 1, mr: 0.5 }}>{selectedIcon}</Flex>
            {params.InputProps?.startAdornment}
          </>
        ) : (
          params.InputProps?.startAdornment
        ),
      }}
    />
  );
};

type RenderOptionProp = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: any,
  state: AutocompleteRenderOptionState,
  ownerState: AutocompleteOwnerState<any, any, any, any>,
) => React.ReactNode;

type RenderOptionConfig = {
  renderOptionProp?: RenderOptionProp;
  keepOpen: React.MutableRefObject<boolean>;
  onOptionAction?: (optionValue: string, actionValue: string) => void;
  closeAutocomplete: () => void;
};

const renderOption = (
  optionProps: React.HTMLAttributes<HTMLLIElement>,
  option: any,
  state: AutocompleteRenderOptionState,
  ownerState: AutocompleteOwnerState<any, any, any, any>,
  {
    renderOptionProp,
    keepOpen,
    onOptionAction,
    closeAutocomplete,
  }: RenderOptionConfig,
) => {
  if (option?.isEmptyPlaceholder) {
    return (
      <Box
        component="li"
        {...optionProps}
        sx={{
          pointerEvents: 'none',
          '&:hover': { backgroundColor: 'transparent !important' },
        }}
      >
        <Typography sx={{ color: 'common.gray4' }}>{option.label}</Typography>
      </Box>
    );
  }

  if (renderOptionProp) {
    return renderOptionProp(optionProps, option, state, ownerState);
  }

  return (
    <OptionWithActions
      props={optionProps}
      option={option}
      keepOpen={keepOpen}
      onOptionAction={onOptionAction}
      closeAutocomplete={closeAutocomplete}
    />
  );
};

type Props = Omit<
  AutocompleteProps<any, any, any, any>,
  'renderInput' | 'size' | 'onChange' | 'options'
> & {
  /** Options may be plain strings or `{ value, label, icon?, actions? }` objects. */
  options?: any[];
  size?: ComponentSize;
  onChange?: (
    event: React.SyntheticEvent,
    value: any,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any>,
  ) => void;
  onOptionAction?: (optionValue: string, actionValue: string) => void;
  actions?: string[];
  onAction?: (actionValue: string) => void;
  variant?: 'borderless';
  listboxMaxWidth?: string | number;
  listboxMaxHeight?: string | number;
  listboxTitle?: string;
  chipDisplay?: boolean;
  showEmptyMessage?: boolean;
  customEmptyMessage?: ReactNode;
  valueValidator?: (value: string) => boolean;
  invalidMessage?: string;
  groups?: string[];
  groupEmptyText?: string;
  label?: string;
  name?: string;
  error?: boolean;
  helperText?: ReactNode;
  /** Multiple mode: allow editing a chip inline by double clicking it. */
  editable?: boolean;
  /** Multiple + freeSolo mode: character(s) used to split typed / pasted text into multiple chips. */
  separator?: string;
  /** Multiple mode: maximum number of suggestions shown in the dropdown. */
  maxOptions?: number;
};

const Autocomplete = forwardRef(
  (
    {
      placeholder,
      onOptionAction,
      actions,
      onAction,
      open: propsOpen,
      onOpen: propsOnOpen,
      onClose: propsOnClose,
      value,
      disableClearable = false,
      variant,
      listboxMaxWidth,
      listboxMaxHeight,
      listboxTitle,
      multiple = false,
      chipDisplay = false,
      onChange,
      showEmptyMessage = true,
      customEmptyMessage,
      valueValidator,
      invalidMessage,
      inputValue: propsInputValue,
      onInputChange: propsOnInputChange,
      size = 'medium',
      options = EMPTY_OPTIONS,
      groups,
      groupEmptyText,
      groupBy: groupByProp,
      label,
      name,
      error = false,
      helperText,
      editable = false,
      separator = ',',
      maxOptions,
      freeSolo = false,
      disabled = false,
      fullWidth = multiple,
      filterOptions: filterOptionsProp,
      filterSelectedOptions = multiple,
      clearOnBlur = !freeSolo,
      className,
      getOptionLabel: getOptionLabelProp,
      defaultValue,
      ...props
    }: Props,
    ref,
  ) => {
    const keepOpen = useRef(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const isEditingRef = useRef(false);
    const helperTextId = useId();
    const [localOpen, setLocalOpen] = useState(false);
    const [localInputValue, setLocalInputValue] = useState('');
    const [placeholderWidth, setPlaceholderWidth] = useState(0);
    const [localValue, setLocalValue] = useState<any>(() => {
      if (defaultValue !== undefined && defaultValue !== null) {
        return defaultValue;
      }
      return multiple || chipDisplay ? [] : null;
    });
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingText, setEditingText] = useState('');
    const [editHighlight, setEditHighlight] = useState(-1);
    const [editAnchor, setEditAnchor] = useState<HTMLInputElement | null>(null);

    const isControlled = propsOpen !== undefined;
    const isInputControlled = propsInputValue !== undefined;
    const isValueControlled = value !== undefined;
    const inputValue = isInputControlled ? propsInputValue : localInputValue;
    const currentValue = isValueControlled ? value : localValue;

    // When chipDisplay is true, we use multiple mode internally but restrict to single value
    const internalMultiple = multiple || chipDisplay;

    const selectedOption = useMemo(() => {
      if (!currentValue || !options) return currentValue;
      const matchOption = (v: any) => {
        const val = typeof v === 'object' ? v.value : v;
        if (val === undefined) return v;
        return (
          options.find(
            (opt) => (typeof opt === 'object' ? opt.value : opt) === val,
          ) || v
        );
      };
      if (Array.isArray(currentValue)) {
        return currentValue.map(matchOption);
      }
      return matchOption(currentValue);
    }, [currentValue, options]);

    // Multiple mode keeps chips in a stable order: the first externally
    // provided value (which may arrive after mount, e.g. async defaults) is
    // sorted once, and anything added later is appended in insertion order.
    const initialOrderRef = useRef<Map<string, number> | null>(null);
    const userChangedRef = useRef(false);
    if (
      initialOrderRef.current === null &&
      (userChangedRef.current ||
        (multiple && Array.isArray(currentValue) && currentValue.length > 0))
    ) {
      initialOrderRef.current = new Map<string, number>(
        userChangedRef.current || !Array.isArray(currentValue)
          ? []
          : [...currentValue]
              .sort((a, b) => itemText(a).localeCompare(itemText(b)))
              .map((v, i): [string, number] => [itemValue(v), i]),
      );
    }
    const initialOrder = initialOrderRef.current ?? EMPTY_ORDER;

    // MUI resets the input whenever the `value` reference changes, so the
    // ordered array keeps its identity while its contents are unchanged.
    const orderedValueRef = useRef<AutocompleteItem[]>([]);
    const orderedValue = useMemo((): AutocompleteItem[] => {
      if (!multiple || !Array.isArray(selectedOption)) return [];
      const known = selectedOption.filter((v) =>
        initialOrder.has(itemValue(v)),
      );
      const added = selectedOption.filter(
        (v) => !initialOrder.has(itemValue(v)),
      );
      known.sort(
        (a, b) =>
          (initialOrder.get(itemValue(a)) ?? 0) -
          (initialOrder.get(itemValue(b)) ?? 0),
      );
      const next = [...known, ...added];
      const prev = orderedValueRef.current;
      const unchanged =
        prev.length === next.length &&
        prev.every((item, i) => item === next[i]);
      if (!unchanged) orderedValueRef.current = next;
      return orderedValueRef.current;
    }, [multiple, selectedOption, initialOrder]);

    const augmentedOptions = useMemo(() => {
      if (!groups?.length || !groupEmptyText || !groupByProp) return options;
      const existingGroups = new Set(options.map((opt) => groupByProp(opt)));
      const result: typeof options[number][] = [];
      groups.forEach((group) => {
        const groupOptions = options.filter(
          (opt) => groupByProp(opt) === group,
        );
        if (groupOptions.length > 0) {
          result.push(...groupOptions);
        } else if (!existingGroups.has(group)) {
          result.push({
            isEmptyPlaceholder: true,
            __placeholderGroup: group,
            label: groupEmptyText,
            value: `__empty_${group}`,
          });
        }
      });
      options.forEach((opt) => {
        if (!groups.includes(groupByProp(opt))) result.push(opt);
      });
      return result;
    }, [options, groups, groupEmptyText, groupByProp]);

    const internalGroupBy = groupByProp
      ? (option: {
          isEmptyPlaceholder?: boolean;
          __placeholderGroup?: string;
          [key: string]: any;
        }) =>
          option.isEmptyPlaceholder
            ? option.__placeholderGroup ?? ''
            : groupByProp(option)
      : undefined;

    const internalValue =
      chipDisplay && !multiple
        ? selectedOption !== null && selectedOption !== undefined
          ? [selectedOption]
          : []
        : multiple
        ? orderedValue
        : selectedOption;

    const hasValue = Array.isArray(internalValue)
      ? internalValue.length > 0
      : internalValue !== null && internalValue !== undefined;

    const getOptionLabel = useCallback(
      (option: any) =>
        getOptionLabelProp ? getOptionLabelProp(option) : itemText(option),
      [getOptionLabelProp],
    );

    const getSuggestions = useCallback(
      (query: string, exclude: AutocompleteItem[]) => {
        if (query.length === 0) return [];
        const excludedKeys = new Set(exclude.map(itemValue));
        const candidates = filterSelectedOptions
          ? augmentedOptions.filter(
              (o: AutocompleteItem) => !excludedKeys.has(itemValue(o)),
            )
          : augmentedOptions;
        const filtered = (filterOptionsProp ?? defaultFilterOptions)(
          [...candidates],
          { inputValue: query, getOptionLabel },
        );
        return maxOptions === undefined
          ? filtered
          : filtered.slice(0, maxOptions);
      },
      [
        augmentedOptions,
        filterSelectedOptions,
        filterOptionsProp,
        getOptionLabel,
        maxOptions,
      ],
    );

    const suggestions = useMemo(
      () => (multiple ? getSuggestions(inputValue, orderedValue) : []),
      [multiple, getSuggestions, inputValue, orderedValue],
    );
    const showNoOptions =
      multiple &&
      showEmptyMessage &&
      !props.loading &&
      options.length > 0 &&
      inputValue.length > 0 &&
      suggestions.length === 0;

    const editSuggestions = useMemo(
      () =>
        editingIndex === null
          ? []
          : getSuggestions(
              editingText,
              orderedValue.filter((_, i) => i !== editingIndex),
            ),
      [getSuggestions, editingText, orderedValue, editingIndex],
    );

    // Multiple mode only shows the dropdown while the user is typing.
    const open = isControlled
      ? propsOpen
      : multiple
      ? inputValue.length > 0 &&
        (suggestions.length > 0 || showNoOptions || !!props.loading)
      : localOpen;

    useLayoutEffect(() => {
      if (chipDisplay && !hasValue && placeholder) {
        const span = document.createElement('span');
        span.style.visibility = 'hidden';
        span.style.position = 'absolute';
        span.style.whiteSpace = 'nowrap';
        span.style.fontSize = '14px';
        span.style.fontFamily = 'Inter, sans-serif';
        span.textContent = placeholder;
        document.body.appendChild(span);
        // 45px to account for padding and arrow icon
        setPlaceholderWidth(span.offsetWidth + 45);
        document.body.removeChild(span);
      }
    }, [chipDisplay, hasValue, placeholder]);

    const handleOpen = (event: React.SyntheticEvent) => {
      if (!isControlled) {
        setLocalOpen(true);
      }
      propsOnOpen?.(event);
    };

    const handleClose = (event: React.SyntheticEvent, reason: any) => {
      if (keepOpen.current) {
        return;
      }
      if (!isControlled) {
        setLocalOpen(false);
      }
      propsOnClose?.(event, reason);
    };

    const toStorableValue = (v: any) =>
      v && typeof v === 'object' && v.value !== undefined
        ? { value: v.value, label: v.label }
        : v;

    const emitChange = (
      event: React.SyntheticEvent,
      newValue: any,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<any>,
    ) => {
      userChangedRef.current = true;
      const valueToStore = Array.isArray(newValue)
        ? newValue.map(toStorableValue)
        : toStorableValue(newValue);
      if (!isValueControlled) {
        setLocalValue(valueToStore);
      }
      onChange?.(event, valueToStore, reason, details);
    };

    // A consumer that controls `inputValue` owns its own parsing of the
    // typed text, so the built-in separator splitting stays out of its way.
    const splitsInput = multiple && freeSolo && !isInputControlled;

    const setInputValue = (
      event: React.SyntheticEvent,
      newInputValue: string,
    ) => {
      if (!isInputControlled) {
        setLocalInputValue(newInputValue);
      }
      propsOnInputChange?.(event, newInputValue, 'input');
    };

    /** Resolves typed text to an option (by label or value), or keeps it as free text when allowed. */
    const resolveItem = (
      candidate: AutocompleteItem,
    ): AutocompleteItem | null => {
      if (typeof candidate !== 'string') return candidate;
      const text = normalizeText(candidate);
      if (text.length === 0) return null;
      const match = options.find(
        (o: AutocompleteItem) => itemText(o) === text || itemValue(o) === text,
      );
      if (match !== undefined) return match;
      return freeSolo ? text : null;
    };

    const addItems = (
      event: React.SyntheticEvent,
      candidates: AutocompleteItem[],
      reason: AutocompleteChangeReason,
    ) => {
      const existing = new Set(orderedValue.map(itemValue));
      const accepted = dedupeItems(
        candidates
          .map(resolveItem)
          .filter((v): v is AutocompleteItem => v !== null),
      ).filter((v) => !existing.has(itemValue(v)));
      if (accepted.length > 0) {
        emitChange(event, [...orderedValue, ...accepted], reason);
      }
    };

    const handleChange = (
      event: React.SyntheticEvent,
      newValue: any,
      reason: MuiAutocompleteChangeReason,
      details?: AutocompleteChangeDetails<any>,
    ) => {
      if (chipDisplay && !multiple) {
        // In chipDisplay mode (single selection), extract the single value from array
        // Take the last item since MUI adds the new selection to the end
        const singleValue = Array.isArray(newValue)
          ? newValue[newValue.length - 1] || null
          : newValue;
        emitChange(event, singleValue, reason, details);
        return;
      }

      if (!multiple || !Array.isArray(newValue)) {
        emitChange(event, newValue, reason, details);
        return;
      }

      if (reason === 'clear') {
        emitChange(event, [], reason, details);
        return;
      }
      if (reason === 'removeOption') {
        const remaining = new Set(newValue.map(itemValue));
        emitChange(
          event,
          orderedValue.filter((v) => remaining.has(itemValue(v))),
          reason,
          details,
        );
        return;
      }
      const existing = new Set(orderedValue.map(itemValue));
      addItems(
        event,
        newValue.filter((v: AutocompleteItem) => !existing.has(itemValue(v))),
        reason,
      );
    };

    const handleInputChange: AutocompleteProps<
      any,
      any,
      any,
      any
    >['onInputChange'] = (event, newInputValue, reason) => {
      if (multiple && keepOpen.current && reason !== 'input') {
        return;
      }
      if (splitsInput && newInputValue.includes(separator)) {
        const parts = newInputValue.split(separator);
        const pending = parts.pop() ?? '';
        addItems(event, parts, 'createOption');
        setInputValue(event, pending.trimStart());
        return;
      }
      if (!isInputControlled) {
        setLocalInputValue(newInputValue);
      }
      propsOnInputChange?.(event, newInputValue, reason);
    };

    const handlePaste = (event: React.ClipboardEvent) => {
      const text = event.clipboardData.getData('text');
      if (!splitsInput || (!text.includes(separator) && !text.includes('\n'))) {
        return;
      }
      event.preventDefault();
      addItems(
        event,
        splitValues(inputValue + text.replace(/\n/g, separator), separator),
        'createOption',
      );
      setInputValue(event, '');
    };

    const startEditing = (index: number) => {
      if (disabled || !editable || !isEditableItem(orderedValue[index])) {
        return;
      }
      isEditingRef.current = true;
      setEditingIndex(index);
      setEditingText(itemText(orderedValue[index]));
      setEditHighlight(-1);
    };

    const stopEditing = (refocus = true) => {
      isEditingRef.current = false;
      setEditingIndex(null);
      setEditingText('');
      setEditHighlight(-1);
      setEditAnchor(null);
      if (refocus) inputRef.current?.focus();
    };

    const commitEdit = (
      event: React.SyntheticEvent,
      override?: AutocompleteItem,
      refocus = true,
    ) => {
      if (!isEditingRef.current) return;
      if (editingIndex === null || editingIndex >= orderedValue.length) {
        stopEditing(refocus);
        return;
      }
      const original = orderedValue[editingIndex];
      const nextText = normalizeText(
        override ? itemText(override) : editingText,
      );
      if (nextText === itemText(original)) {
        stopEditing(refocus);
        return;
      }
      if (nextText.length === 0) {
        emitChange(
          event,
          orderedValue.filter((_, i) => i !== editingIndex),
          'removeOption',
          { option: original },
        );
        stopEditing(refocus);
        return;
      }
      const next = override ?? resolveItem(nextText);
      if (next !== null) {
        const order = initialOrderRef.current;
        const position = order?.get(itemValue(original));
        if (order && position !== undefined) {
          order.delete(itemValue(original));
          order.set(itemValue(next), position);
        }
        emitChange(
          event,
          dedupeItems(
            orderedValue.map((v, i) => (i === editingIndex ? next : v)),
          ),
          'editOption',
          { option: next },
        );
      }
      stopEditing(refocus);
    };

    const handleEditKeyDown = (event: React.KeyboardEvent) => {
      event.stopPropagation();
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (editSuggestions.length === 0) return;
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const cycle = editSuggestions.length + 1;
        setEditHighlight((h) => ((h + 1 + step + cycle) % cycle) - 1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (editHighlight >= 0 && editHighlight < editSuggestions.length) {
          commitEdit(event, editSuggestions[editHighlight]);
          return;
        }
        const nextText = normalizeText(editingText);
        if (nextText.length > 0 && resolveItem(nextText) === null) {
          return;
        }
        commitEdit(event);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        stopEditing();
      }
    };

    const renderTags = (
      tags: AutocompleteItem[],
      getTagProps: AutocompleteRenderGetTagProps,
    ) =>
      tags.map((tag, index) => {
        const { key, onDelete, ...tagProps } = getTagProps({ index });
        const tagLabel = itemLabel(tag);

        if (multiple && editingIndex === index) {
          return (
            <EditInput
              key={key}
              ref={setEditAnchor}
              $size={size}
              aria-label="edit chip"
              autoFocus
              size={Math.max(editingText.length, 1)}
              value={editingText}
              onChange={(e) => {
                setEditingText(e.target.value);
                setEditHighlight(-1);
              }}
              onKeyDown={handleEditKeyDown}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onBlur={(e) => commitEdit(e, undefined, false)}
            />
          );
        }

        if (valueValidator && !valueValidator(itemValue(tag))) {
          return (
            <InvalidChipWrapper
              key={key}
              $size={size}
              onClick={(e) => {
                e.stopPropagation();
                if (disabled) return;
                setInputValue(e, itemText(tag));
                emitChange(
                  e,
                  tags.filter((_, i) => i !== index),
                  'removeOption',
                  { option: tag },
                );
              }}
            >
              {tagLabel}
            </InvalidChipWrapper>
          );
        }

        if (!multiple) {
          return (
            <Chip
              key={key}
              customVariant="rounded"
              label={tagLabel}
              icon={itemIcon(tag)}
              bold
              onDelete={onDelete}
              {...tagProps}
            />
          );
        }

        return (
          <TagChip
            key={key}
            {...tagProps}
            label={tagLabel}
            icon={itemIcon(tag)}
            title={itemText(tag)}
            size={size === 'large' ? undefined : 'medium'}
            customVariant="rounded"
            onDelete={disabled ? undefined : onDelete}
            onDoubleClick={() => startEditing(index)}
          />
        );
      });

    const minWidth = useMemo(
      () => (!hasValue && placeholderWidth ? `${placeholderWidth}px` : '0px'),
      [hasValue, placeholderWidth],
    );

    const hasInvalidValues =
      valueValidator &&
      Array.isArray(internalValue) &&
      internalValue.some((item) => !valueValidator(itemValue(item)));

    return (
      <Wrapper
        className={multiple ? className : undefined}
        $size={size}
        $multiple={multiple}
        $fullWidth={fullWidth}
      >
        <AutocompleteBase
          sx={{ minWidth }}
          className={multiple ? undefined : className}
          aria-label="autocomplete"
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          multiple={internalMultiple}
          value={internalValue}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          size={size === 'large' ? 'medium' : 'small'}
          options={augmentedOptions}
          getOptionLabel={getOptionLabel}
          groupBy={internalGroupBy}
          freeSolo={freeSolo}
          disabled={disabled}
          fullWidth={fullWidth}
          clearOnBlur={clearOnBlur}
          filterSelectedOptions={filterSelectedOptions}
          filterOptions={multiple ? () => suggestions : filterOptionsProp}
          handleHomeEndKeys={multiple || !freeSolo}
          forcePopupIcon={multiple ? false : 'auto'}
          getOptionDisabled={(option: any) =>
            !!option?.isEmptyPlaceholder ||
            !!(props.getOptionDisabled && props.getOptionDisabled(option))
          }
          PaperComponent={(paperProps) => (
            <Paper
              {...paperProps}
              actions={actions}
              onAction={onAction}
              handleClose={handleClose}
              noOptionsMessage={
                showNoOptions
                  ? customEmptyMessage ?? NO_OPTIONS_TEXT
                  : undefined
              }
            />
          )}
          ListboxComponent={
            listboxTitle
              ? forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLElement>>(
                  (listboxProps, listboxRef) => (
                    <ul {...listboxProps} ref={listboxRef}>
                      <Box
                        component="li"
                        sx={{
                          padding: '6px 16px',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: 'common.gray4',
                          pointerEvents: 'none',
                        }}
                      >
                        {listboxTitle}
                      </Box>
                      {listboxProps.children}
                    </ul>
                  ),
                )
              : undefined
          }
          renderTags={(tags, getTagProps) =>
            renderTags(tags as AutocompleteItem[], getTagProps)
          }
          renderInput={(params) =>
            renderInput(params, {
              ref,
              inputRef,
              autoFocus: props.autoFocus,
              placeholder,
              variant,
              chipDisplay,
              multiple,
              hasValue,
              value: selectedOption,
              size,
              label,
              name,
              error,
              helperTextId: helperText ? helperTextId : undefined,
              onPaste: multiple && freeSolo ? handlePaste : undefined,
            })
          }
          renderOption={(optionProps, option, state, ownerState) =>
            renderOption(optionProps, option, state, ownerState, {
              renderOptionProp: props.renderOption,
              keepOpen,
              onOptionAction,
              closeAutocomplete: () => {
                if (multiple && !isInputControlled) {
                  setLocalInputValue('');
                }
                if (!isControlled) {
                  setLocalOpen(false);
                }
              },
            })
          }
          componentsProps={{
            popper: {
              sx: {
                '&.MuiPopper-root': { mt: '8px !important' },
                ...(listboxMaxWidth && { width: 'fit-content !important' }),
                ...(!showEmptyMessage && {
                  '.MuiAutocomplete-noOptions': {
                    display: 'none !important',
                  },
                }),
              },
              ...(listboxMaxWidth && {
                placement: 'bottom-start',
              }),
            },
          }}
          ListboxProps={{
            sx: {
              padding: '4px',
              ...(listboxMaxWidth && { maxWidth: listboxMaxWidth }),
              ...(listboxMaxHeight && { maxHeight: listboxMaxHeight }),
              '.MuiAutocomplete-option': {
                display: 'block',
                fontSize: '14px',
                padding: '10px 16px',
                borderRadius: '8px',
                backgroundColor: 'white !important',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '.actions-menu-button': {
                  visibility: 'hidden',
                },
                ':hover': {
                  backgroundColor: (theme) =>
                    `${theme.palette.common.gray9} !important`,
                  '.actions-menu-button': {
                    visibility: 'visible',
                  },
                },
                '&.menu-open .actions-menu-button': {
                  visibility: 'visible',
                },
              },
              '& .Mui-focusVisible': {
                backgroundColor: (theme) =>
                  `${theme.palette.common.gray9} !important`,
              },
              ...(!showEmptyMessage && {
                '.MuiAutocomplete-noOptions': {
                  display: 'none',
                },
              }),
            },
          }}
          renderGroup={(params) => (
            <Box key={params.key}>
              {Number(params.key) > 0 && <Divider sx={{ my: 0.5 }} />}
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '12px',
                  color: 'common.gray11',
                  padding: '8px 16px 4px',
                }}
              >
                {params.group}
              </Typography>
              {params.children}
            </Box>
          )}
          popupIcon={<KeyboardArrowDown />}
          disableClearable={disableClearable}
          noOptionsText={
            showEmptyMessage
              ? customEmptyMessage
                ? customEmptyMessage
                : undefined
              : null
          }
          {...props}
        />
        {multiple && editingIndex !== null && editAnchor && options.length > 0 && (
          <Popper
            open={editingText.length > 0}
            anchorEl={editAnchor}
            placement="bottom-start"
            sx={{ mt: '8px !important', zIndex: 'modal' }}
          >
            <PaperBase>
              {editSuggestions.length === 0 ? (
                <NoOptions>{customEmptyMessage ?? NO_OPTIONS_TEXT}</NoOptions>
              ) : (
                <SuggestionList role="listbox" aria-label="edit suggestions">
                  {editSuggestions.map((option, i) => (
                    <SuggestionItem
                      key={itemValue(option)}
                      role="option"
                      aria-selected={i === editHighlight}
                      $highlighted={i === editHighlight}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => commitEdit(e, option)}
                    >
                      {itemLabel(option)}
                    </SuggestionItem>
                  ))}
                </SuggestionList>
              )}
            </PaperBase>
          </Popper>
        )}
        {helperText && (
          <FormHelperText id={helperTextId} error={error} disabled={disabled}>
            {helperText}
          </FormHelperText>
        )}
        {hasInvalidValues && (
          <FormControls.ErrorMessage>
            {invalidMessage || 'Invalid values selected.'}
          </FormControls.ErrorMessage>
        )}
      </Wrapper>
    );
  },
);

export default Autocomplete;
